import React from 'react';
import { Box, Typography } from '@availity/element';
import usersIcon from '../../../static/assets/icons/users.svg';
import hospitalIcon from '../../../static/assets/icons/hospital.svg';
import databaseIcon from '../../../static/assets/icons/database.svg';
import socIcon from '../../../static/assets/logos/soc.png';
import hipaaCompliantIcon from '../../../static/assets/logos/HipaaCompliant.svg';
import securityBg from '../../../static/assets/backgrounds/security-bg.png';
import { colors, iconFilters } from '../../../static/constants/theme';

interface SecurityFeatureProps {
  title: string;
  description: string;
}

interface SecurityCardProps {
  title: string;
  description: string;
  icon: string;
}

const iconMap = {
  'HIPAA Compliance': hospitalIcon,
  'Data Encryption': databaseIcon,
  'Access Control': usersIcon,
};

const SecurityFeature: React.FC<SecurityFeatureProps> = ({ title, description }) => (
  <Box className="security-feature">
    <Box className="security-feature__icon-wrapper" sx={{ background: colors.accent.orangePeach }}>
      {iconMap[title] && (
        <Box 
          component="img"
          src={iconMap[title]}
          alt=""
          className="security-feature__icon"
          sx={{ filter: iconFilters.orange }}
        />
      )}
    </Box>
    <Box>
      <Typography className="security-feature__title">
        {title}
      </Typography>
      <Typography className="security-feature__description">
        {description}
      </Typography>
    </Box>
  </Box>
);

const SecurityCard: React.FC<SecurityCardProps> = ({ title, description, icon }) => (
  <Box className="security-card" sx={{ background: colors.background.dark, borderColor: colors.border.primary, minHeight: 180 }}>
    <Box className="security-card__logo">
      {icon && <Box component="img" src={icon} alt={`${title} certification logo`} />}
    </Box>
    <Box className="security-card__content">
      <Typography className="security-card__title" sx={{ color: colors.white }}>
        {title}
      </Typography>
      <Typography className="security-card__description" sx={{ color: colors.white }}>
        {description}
      </Typography>
    </Box>
  </Box>
);

const SecuritySection: React.FC = () => (
  <Box 
    className="security-section" 
    sx={{ 
      py: { xs: 8, md: 16.5 },
      background: colors.background.light,
      '&::before': {
        backgroundImage: `url(${securityBg})`,
      },
    }}
  >
    <Box className="security-section__container" sx={{ maxWidth: 976, gap: 2 }}>
      <Typography className="section-subtitle section-title--orange">
        Secure
      </Typography>
      <Typography className="section-title section-title--dark">
        Availity healthcare APIs are built with enterprise-grade security
      </Typography>
    </Box>

    <Box className="security-section__container" sx={{ flexDirection: { xs: 'column', sm: 'row' }, gap: 5, mt: { xs: 8, md: 13.5 }, alignItems: 'center' }}>
      <SecurityFeature
        title="HIPAA Compliance"
        description="All APIs adhere to strict healthcare data protection standards."
      />
      <SecurityFeature
        title="Data Encryption"
        description="End-to-end encryption ensures secure data exchange."
      />
      <SecurityFeature
        title="Access Control"
        description="Role-based permissions safeguard sensitive information."
      />
    </Box>

    <Box className="security-section__container" sx={{ flexDirection: { xs: 'column', sm: 'row' }, gap: 3, mt: { xs: 8, md: 13.5 } }}>
      <SecurityCard
        title="SOC 2 Certified"
        description="Independently examined SOC 2 controls align with AICPA Trust Services Criteria for security, availability, and confidentiality."
        icon={socIcon}
      />
      <SecurityCard
        title="HIPAA Compliant"
        description="Administrative, technical, and physical safeguards align with the HIPAA Security Rule."
        icon={hipaaCompliantIcon}
      />
    </Box>
  </Box>
);

export default SecuritySection;